import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { 
  Send, 
  Calendar, 
  Mail, 
  Phone, 
  MapPin,
  MessageCircle,
  CheckCircle,
  Loader2
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALENDLY_URL = "https://calendly.com/vmehta03/northstack?month=2026-03";

const services = [
  { value: "app-acquisition", label: "App User Acquisition" },
  { value: "performance-marketing", label: "Performance Marketing" },
  { value: "analytics", label: "Analytics & Attribution" },
  { value: "retention", label: "Retention & Growth" },
  { value: "other", label: "Other" },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        service: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-[#1A1F26]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm text-[#007AFF] font-medium uppercase tracking-wider">Contact</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
            Ready to Scale Your <span className="gradient-text">Revenue?</span>
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto text-lg">
            Let's discuss your growth goals. Book a free 30-minute consultation call to explore 
            how we can help you drive user acquisition and maximize ROAS.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {isSubmitted ? (
              <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#00C853]/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-[#00C853]" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Thank You!</h3>
                <p className="text-[#8B949E] mb-6">
                  Your message has been received. We'll get back to you within 24 hours.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="border-[#30363D] hover:bg-white/5"
                  data-testid="contact-send-another-btn"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="bg-[#242B33] border-[#30363D] focus:border-[#007AFF]"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="bg-[#242B33] border-[#30363D] focus:border-[#007AFF]"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Company</label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your company"
                      className="bg-[#242B33] border-[#30363D] focus:border-[#007AFF]"
                      data-testid="contact-company-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+971 XX XXX XXXX"
                      className="bg-[#242B33] border-[#30363D] focus:border-[#007AFF]"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Service Interested In</label>
                  <Select onValueChange={handleServiceChange} value={formData.service}>
                    <SelectTrigger 
                      className="bg-[#242B33] border-[#30363D] focus:border-[#007AFF]"
                      data-testid="contact-service-select"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#242B33] border-[#30363D]">
                      {services.map((service) => (
                        <SelectItem 
                          key={service.value} 
                          value={service.value}
                          className="hover:bg-white/5"
                        >
                          {service.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project and goals..."
                    required
                    rows={5}
                    className="bg-[#242B33] border-[#30363D] focus:border-[#007AFF] resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full gradient-btn py-6 text-lg font-semibold"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>

          {/* Right - Contact Info & Calendly */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            {/* Calendly CTA */}
            <div className="bg-gradient-to-br from-[#007AFF]/10 to-[#00C853]/10 rounded-2xl border border-[#007AFF]/30 p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007AFF] to-[#00C853] flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Book a Free Consultation</h3>
                  <p className="text-[#A1A1AA]">
                    Schedule a 30-minute call to discuss your growth goals and get a customized strategy.
                  </p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6 text-sm text-[#A1A1AA]">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#00C853]" />
                  Free 30-minute consultation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#00C853]" />
                  Customized growth strategy
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#00C853]" />
                  No obligation, just insights
                </li>
              </ul>

              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="gradient-btn w-full py-4 text-center font-semibold inline-flex items-center justify-center gap-2"
                data-testid="calendly-link"
              >
                <Calendar className="w-5 h-5" />
                Book Your Free Consultation
              </a>
            </div>

            {/* Contact Info Cards */}
            <div className="grid gap-4">
              <div className="bg-[#242B33] rounded-xl border border-[#30363D] p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#007AFF]" />
                </div>
                <div>
                  <p className="text-sm text-[#8B949E]">Email</p>
                  <a href="mailto:hello@northstack.agency" className="font-medium hover:text-[#007AFF] transition-colors">
                    hello@northstack.agency
                  </a>
                </div>
              </div>

              <div className="bg-[#242B33] rounded-xl border border-[#30363D] p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#00C853]/10 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#00C853]" />
                </div>
                <div className="flex-1 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-[#8B949E]">Phone</p>
                    <a href="tel:+971522361008" className="font-medium hover:text-[#007AFF] transition-colors">
                      +971-522361008
                    </a>
                  </div>
                  <a
                    href="https://wa.me/917011702848?text=Hi%20North%20Stack%2C%20I%27d%20like%20to%20discuss%20a%20performance%20marketing%20project."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] text-sm font-medium hover:bg-[#25D366]/20 transition-colors"
                    data-testid="contact-whatsapp-link"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp: +91-7011702848
                  </a>
                </div>
              </div>

              <div className="bg-[#242B33] rounded-xl border border-[#30363D] p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#007AFF]" />
                </div>
                <div>
                  <p className="text-sm text-[#8B949E]">Location</p>
                  <p className="font-medium">Dubai HQ, India</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
